import React, { useState } from "react";
import { Idea } from "../../../model/types";
import { useOwnerInvitationsViewModel } from "../../../viewmodel/useOwnerInvitationsViewModel";
import { useOrganizationSearchViewModel } from "../../../viewmodel/useOrganizationSearchViewModel";
import { RequestsSection } from "./RequestsSection";
import { InvitationsSection } from "./InvitationsSection";
import { InviteOrganizationsForm } from "./InviteOrganizationsForm";
import { PartnershipList } from "./PartnershipList";

import { setModalIsOpened } from "@/core/store/app";
import { OrganizationSearchModal } from "../../modals/OrganizationSearchModal";

interface PartnershipTabProps {
  ideaId: string | undefined;
  idea: Idea;
  ideaActions: any;
}

export const PartnershipTab: React.FC<PartnershipTabProps> = ({
  ideaId,
  idea,
  ideaActions,
}) => {
  const [showOrgSearchModal, setShowOrgSearchModal] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [makingCoordinator, setMakingCoordinator] = useState<string | null>(
    null
  );

  const {
    invitations,
    loading,
    withdrawingInvitation,
    processingRequest,
    emailInputs,
    invitationsLoading,
    withdrawInvitation,
    acceptRequest,
    denyRequest,
    addEmailInput,
    updateEmailInput,
    sendInvitations,
  } = useOwnerInvitationsViewModel(ideaId, idea.is_owner);

  const orgSearch = useOrganizationSearchViewModel(ideaId, () => {
    setShowOrgSearchModal(false);
    setModalIsOpened(false);
  });

  const handleRemoveMember = async (memberId: string) => {
    setRemovingMember(memberId);
    await ideaActions.removeMember(memberId);
    setRemovingMember(null);
  };

  const handleMakeCoordinator = async (partnerId: string) => {
    setMakingCoordinator(partnerId);
    await ideaActions.makeCoordinator(partnerId);
    setMakingCoordinator(null);
  };

  const handleOpenOrgSearch = () => {
    setShowOrgSearchModal(true);
    setModalIsOpened(true);
  };

  const handleCloseOrgSearch = () => {
    setShowOrgSearchModal(false);
    setModalIsOpened(false);
    orgSearch.resetSearch();
  };

  return (
    <>
      <div className="partnership-tab">
        {idea.is_owner && (
          <>
            <RequestsSection
              requests={invitations?.request || {}}
              loading={loading}
              processingRequest={processingRequest}
              onAccept={acceptRequest}
              onDeny={denyRequest}
            />

            <InvitationsSection
              invitations={invitations?.invitation || {}}
              loading={loading}
              withdrawingInvitation={withdrawingInvitation}
              onWithdraw={withdrawInvitation}
            />

            {!loading &&
              invitations &&
              (!invitations.request ||
                Object.keys(invitations.request).length === 0) &&
              (!invitations.invitation ||
                Object.keys(invitations.invitation).length === 0) && (
                <div className="tab-row">
                  <div className="text-center py-4 text-gray-500">
                    No pending requests or invitations
                  </div>
                </div>
              )}

            {idea?.state.toLowerCase() === "approved" && (
              <InviteOrganizationsForm
                emailInputs={emailInputs}
                invitationsLoading={invitationsLoading}
                onAddEmailInput={addEmailInput}
                onUpdateEmailInput={updateEmailInput}
                onSendInvitations={sendInvitations}
                onSearchOrganization={handleOpenOrgSearch}
              />
            )}
          </>
        )}

        <PartnershipList
          idea={idea}
          onRemoveMember={handleRemoveMember}
          onMakeCoordinator={handleMakeCoordinator}
          removingMember={removingMember}
          makingCoordinator={makingCoordinator}
        />
      </div>

      <OrganizationSearchModal
        isOpen={showOrgSearchModal}
        onClose={handleCloseOrgSearch}
        orgSearch={orgSearch}
      />
    </>
  );
};
